import type { InferOutput } from "valibot";
import { object, string } from "valibot";
import { MAX_DATA_SOURCE_SIZE, MIME_TYPE_EXCEL } from "../../../constants";
import type { DataSourceDefinition } from "../../../types/DataSource";
import { TextInput } from "../../../components/inputs/TextInput";
import FileInput from "../../../components/inputs/FileInput";
import { Checkbox } from "../../../components/checkbox/Checkbox";
import { isNil, noop } from "lodash-es";
import { getOwner, runWithOwner } from "solid-js";
import axios from "axios";
import { triggerBrowserDownload } from "../../../utils/uploadUtil";
import { useTranslation } from "../../../state/i18n";
import { keyFromSelector } from "i18next";
import ApiService from "../../../services/ApiService";
import { baseDataSourceSchema } from "../../../utils/validation/dataSource";
import { require } from "../../../utils/validation/base";

const schema = object({
  ...baseDataSourceSchema.entries,
  file: require(
    object({
      // Under the hood this is a `PendingCustomerResource`
      name: string(),
      fileName: string(),
      source: string(),
    }),
  ),
});

export type ExcelDataSourceConfigurationDto = object;

const ExcelDataSource: DataSourceDefinition<InferOutput<typeof schema>, ExcelDataSourceConfigurationDto> = {
  name: keyFromSelector(($) => $.DATASOURCES_EXCEL_NAME),
  type: "EXCEL",
  icon: "excelLogo",
  schema,
  initialValue: {
    name: undefined,
    file: undefined,
    availableOffline: false,
  },
  onCreated: (dataSource, { file }) => ApiService.importDataSourceFile(dataSource.id, file),
  toConfiguration: (input) => ({
    name: input.name,
    file: null as never, // ! This is a hack since we do not store the file for later use
    availableOffline: input.searchType === "LOCAL_FALLBACK",
  }),
  toDto: (input) => {
    const { name, availableOffline } = input;

    return {
      name,
      searchType: availableOffline ? "LOCAL_FALLBACK" : "LIVE",
    };
  },
  EditItem: (props) => {
    const owner = getOwner();
    const { t } = useTranslation();

    return (
      <div class="flex flex-col gap-6">
        <TextInput
          label={t(($) => $.DATASOURCE_INPUT_NAME)}
          value={props.value("name")}
          required
          onBlur={(e) => props.updateValue("name", e.target.value)}
          onInput={(e) => props.updateValue("name", e.target.value)}
          errorMessage={props.error("name")}
        />

        <FileInput
          label={
            props.type === "CREATE"
              ? t(($) => $.DATASOURCE_EXCEL_CREATE_INPUT_FILE)
              : t(($) => $.DATASOURCE_EXCEL_EDIT_INPUT_FILE)
          }
          value={
            !isNil(props.value("file"))
              ? {
                  filename: props.value("file")!.fileName,
                  mediaTypeIcon: "excelLogo",
                }
              : undefined
          }
          maxFileSizeInBytes={MAX_DATA_SOURCE_SIZE}
          onFileSelected={(file) => Promise.resolve(props.updateValue("file", file))}
          onUploadCancel={noop}
          downloadFile={() => {
            const file = props.value("file");

            if (!isNil(file)) {
              runWithOwner(owner, async () => {
                const client = axios.create();
                await triggerBrowserDownload(client, file.source, file.fileName);
              });
            }
          }}
          errorMessage={props.error("file")}
          required
          accept={[MIME_TYPE_EXCEL]}
          uploadActionLabel={
            props.type === "CREATE"
              ? t(($) => $.DATASOURCE_EXCEL_CREATE_INPUT_FILE_ACTION_UPLOAD)
              : t(($) => $.DATASOURCE_EXCEL_EDIT_INPUT_FILE_ACTION_UPLOAD)
          }
        />

        <Checkbox
          label={t(($) => $.DATASOURCE_INPUT_AVAILABLE_OFFLINE)}
          checked={props.value("availableOffline") === true}
          setChecked={(value) => props.updateValue("availableOffline", value)}
        />
      </div>
    );
  },
};

export default ExcelDataSource;
